import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { Alert, AlertTitle, Box, Section, SnackbarManager, Stack } from '@cmg/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { Fragment, useMemo } from 'react';

import Loading from '../../../../../../common/components/indicators/loading/Loading';
import ServerErrorsBanner from '../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { useFeatureToggles } from '../../../../../../common/config';
import { useCheckFinalInstitutionalAllocationAccess } from '../../../../../../common/util/check-access/useCheckFinalInstitutionalAllocationAccess';
import { isEmeaRegion } from '../../../../../../common/util/region';
import {
  AllocationAcknowledgement,
  IndicationStatus,
  InstitutionalIndicationOrderType,
  MdlAllocationRanking,
  OfferingStatus,
} from '../../../../../../graphql';
import { useOfferingInstrument } from '../../../../../offering/hooks/useOfferingInstrument';
import { isIndicationStatusAndTypeValid } from '../../../../../order-book/utils';
import AllocationRanking from '../../../../components/allocation-ranking/AllocationRanking';
import { useIndicationActivityBuySideContext } from '../../context/IndicationActivityBuySideContext';
import AllocationSection from '../allocation/AllocationSection';
import AllocationDetails from '../allocation/components/allocation-details/AllocationDetails';
import UnderwritingCommitmentDetails from '../allocation/components/underwriting-commitment-details/UnderwritingCommitmentDetails';
import getManagerEconomicsBreakdown from '../allocation/hooks/getManagerEconomicsBreakdown';
import useBuySideAllocation from '../allocation/hooks/useBuySideAllocation';
import { useIsBuySideRestrictedFromSeeingAllocation } from '../allocation/hooks/useIsBuySideRestrictedFromSeeingAllocation';
import IndicationSectionCreateView from './buy-side/indication-section-create-view/IndicationSectionCreateView';
import IndicationSectionEditView from './buy-side/indication-section-edit-view/IndicationSectionEditView';
import IndicationSectionReadView from './buy-side/indication-section-read-view/IndicationSectionReadView';
import {
  useBuySideEconomicsBreakdownQuery,
  useManageIndication_MdlAllocationRankingLazyQuery,
} from './graphql/__generated__';
import { useSetAllocationRankingOnOfferingMutation } from './hooks/useSetAllocationRankingOnOfferingMutation';

type AllocationFormValues = {
  allocationRanking: MdlAllocationRanking;
};

export type Props = Readonly<{
  isOrderBookOpen: boolean;
}>;

const ManageBuySideIndication: React.FC<Props> = ({ isOrderBookOpen }) => {
  const {
    offeringId,
    publishedOffering,
    basicPublishedOffering,
    complianceWarning: { hasComplianceWarning },
  } = useIndicationActivityBuySideContext();
  const pricingInstrument = useOfferingInstrument(publishedOffering);

  const offering = basicPublishedOffering!;
  const indication = offering.myInstitutionalIndication;

  const { canRead: canReadFinalInstitutionalAllocation } =
    useCheckFinalInstitutionalAllocationAccess({
      offeringId: offering.id,
    });

  const { isOrderBookBuySideInvestorReplyOn, isBuySideEconomicsOn } = useFeatureToggles();
  const hasIoiRead = useCheckPermissions([permissionsByEntity.Ioi.READ]);
  const showBuySideEconomics = !isEmeaRegion(pricingInstrument?.region);

  const [isEditingIndication, setIsEditingIndication] = React.useState(false);

  const finalAllocation = indication?.finalAllocation;
  const isFinalAllocationAcknowledged =
    finalAllocation?.investorReply?.status === AllocationAcknowledgement.Acknowledged;
  const isAllocationReleased = !isNil(finalAllocation);
  const hasAllocatedShares = (finalAllocation?.shareQuantity ?? 0) > 0;

  const { data: breakdownQueryData } = useBuySideEconomicsBreakdownQuery({
    variables: {
      offeringId: offeringId,
    },
    skip: !isAllocationReleased,
  });
  const economicsOffering = breakdownQueryData?.publishedOfferings?.items?.[0];

  const managerEconomicsBreakdown = getManagerEconomicsBreakdown(
    economicsOffering?.managerEconomics?.underwritingCommitmentsDistribution ?? [],
    economicsOffering?.syndicate?.managers ?? []
  );

  const [
    getAllocationRanking,
    {
      data: allocationRankingData,
      loading: getAllocationRankingLoading,
      error: getAllocationRankingError,
    },
  ] = useManageIndication_MdlAllocationRankingLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [
    setAllocationRankingOnOffering,
    { loading: setAllocationRankingOnOfferingLoading, error: setAllocationRankingOnOfferingError },
  ] = useSetAllocationRankingOnOfferingMutation({
    onCompleted: () => {
      SnackbarManager.success('Successfully updated allocation ranking');
    },
  });

  const onChangeAllocationRanking = (ranking: MdlAllocationRanking) => {
    setAllocationRankingOnOffering({
      variables: { id: allocationRankingData?.mdlAllocationRanking?.id!, ranking },
    });
  };

  const allocationRanking = allocationRankingData?.mdlAllocationRanking?.ranking;

  const formik = useFormik<AllocationFormValues>({
    initialValues: {
      allocationRanking: defaultTo(allocationRanking, MdlAllocationRanking.NotIndicated),
    },
    onSubmit: values => {
      onChangeAllocationRanking(values.allocationRanking);
    },
  });

  React.useEffect(() => {
    if (isFinalAllocationAcknowledged && hasIoiRead) {
      getAllocationRanking({ variables: { offeringId } });
    }
  }, [
    finalAllocation,
    offeringId,
    getAllocationRanking,
    hasIoiRead,
    isFinalAllocationAcknowledged,
  ]);

  const investorReplyStatus = defaultTo(
    finalAllocation?.investorReply?.status,
    AllocationAcknowledgement.Unacknowledged
  );

  const allocationSectionProps = useBuySideAllocation({
    offeringId,
    allocationVersion: finalAllocation?.version,
    getAllocationRanking,
    investorReplyStatus,
  });

  const displayAllocationButtons = useMemo(() => {
    if (!isAllocationReleased || !finalAllocation || !isOrderBookBuySideInvestorReplyOn) {
      return false;
    }
    return (
      isOrderBookBuySideInvestorReplyOn &&
      indication &&
      isIndicationStatusAndTypeValid(indication.status, indication.type)
    );
  }, [finalAllocation, indication, isAllocationReleased, isOrderBookBuySideInvestorReplyOn]);

  const loading = setAllocationRankingOnOfferingLoading || getAllocationRankingLoading;

  const isCreatingIndication = !indication && !hasComplianceWarning;

  const displayBuySideRestrictedAllocationBanner = useIsBuySideRestrictedFromSeeingAllocation({
    pricingDate: offering.pricingDate,
    settlementAgentCmgEntityKey: offering.syndicate.managerResponsibilities.settlementAgent,
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <div aria-label="Manage Indication">
      <Stack gap={1}>
        {indication?.status === IndicationStatus.Duplicate && (
          <Box mb={2}>
            <Alert severity="warning">
              <AlertTitle>This indication is marked as a duplicate.</AlertTitle>
              You will not receive your final allocation via XC for this offering. Please reach out
              to your sell side sales representative to receive your allocation.
            </Alert>
          </Box>
        )}
        {setAllocationRankingOnOfferingError && (
          <ServerErrorsBanner error={setAllocationRankingOnOfferingError} />
        )}
        {getAllocationRankingError && <ServerErrorsBanner error={getAllocationRankingError} />}
      </Stack>
      {isCreatingIndication && isOrderBookOpen && <IndicationSectionCreateView />}
      {indication && isEditingIndication && isOrderBookOpen && (
        <IndicationSectionEditView
          onFinishEditing={() => setIsEditingIndication(false)}
          hasAllocationRanking={!!allocationRankingData?.mdlAllocationRanking?.ranking}
        />
      )}
      {indication && !isEditingIndication && (
        <IndicationSectionReadView
          onStartEditing={() => setIsEditingIndication(true)}
          hasAllocationRanking={!!allocationRankingData?.mdlAllocationRanking?.ranking}
        />
      )}

      {canReadFinalInstitutionalAllocation &&
        indication &&
        indication.type !== InstitutionalIndicationOrderType.Pass &&
        (displayBuySideRestrictedAllocationBanner ? (
          <Section title="Expected Allocation" data-testid="restricted-allocation-banner">
            <Alert severity="warning">
              The bookrunner(s) have disabled allocation syncing for this offering, so they will not
              be made available via CMG XC. Please reach out to your contact(s) at the bookrunner(s)
              for details regarding your expected allocation.
            </Alert>
          </Section>
        ) : (
          <Fragment>
            <AllocationSection
              sectionTitle="Expected Allocation"
              isReleased={isAllocationReleased}
              displayAllocationActions={displayAllocationButtons}
              {...allocationSectionProps}
            >
              <AllocationDetails
                offeringId={offeringId}
                isReleased={isAllocationReleased}
                allocationData={{
                  offerPrice: offering.offerPrice!,
                  shareQuantity: defaultTo(finalAllocation?.shareQuantity, null),
                  pricingCurrencyCode: offering.pricingCurrencyCode,
                  orderType: indication.type,
                  indicationStatus: indication.status,
                  isOrderBookOpen,
                  investorReplyStatus,
                  ...(isAllocationReleased
                    ? {
                        sellingConcession: defaultTo(
                          economicsOffering?.terms.underwritingFeesAndDiscounts?.sellingConcession,
                          undefined
                        ),
                        tradeDate: defaultTo(economicsOffering?.timing.tradeDate, undefined),
                        settlementDate: defaultTo(
                          economicsOffering?.timing.settlementDate,
                          undefined
                        ),
                      }
                    : {}),
                }}
              />
              {isBuySideEconomicsOn &&
                showBuySideEconomics &&
                isAllocationReleased &&
                (isEmpty(managerEconomicsBreakdown) ? (
                  hasAllocatedShares && (
                    <Alert>
                      The underwriters on this offering have not published underwriting commitments
                      via XC
                    </Alert>
                  )
                ) : (
                  <UnderwritingCommitmentDetails
                    managerEconomicsBreakdown={managerEconomicsBreakdown}
                  />
                ))}
            </AllocationSection>
            {allocationRanking && offering.status === OfferingStatus.Priced && (
              <FormikProvider value={formik}>
                <Form>
                  <AllocationRanking onChangeAllocationRanking={onChangeAllocationRanking} />
                </Form>
              </FormikProvider>
            )}
          </Fragment>
        ))}
    </div>
  );
};

export default ManageBuySideIndication;
